import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';

import { dataLayerConfig, get12HourTime } from '@shared-components-web';
import { Form } from '@shared-forms-web';
import ReasonForVisitForm from '@src/components/Forms/ReasonForVisitForm';
import AppointmentConflictDialog from '@src/components/ReasonForVisit/AppointmentConflictDialog';
import ChangeVisitReasonModal from '@src/components/ReasonForVisit/ChangeVisitReasonModal';
import IllnessExplanationModal from '@src/components/shared/IllnessExplanationModal';
import PreselectedAppointmentDateAlert from '@src/components/shared/PreselectedAppointmentDateAlert';
import { Appointment } from '@src/components/UserPortal/UserPortal.types';
import { useAppointmentConflict } from '@src/hooks/useAppointmentConflict';
import { useReasonForVisitForm } from '@src/hooks/useReasonForVisitForm';
import { getLogger } from '@src/lib/logging/logger';
import { usePetsInformationStore, useTimeSlotStore } from '@src/store';
import { ReasonForVisitType } from '@src/store/modules/reason-for-visit.store';
import { APPLICATION_ROUTES } from '@src/store/modules/route-handler.store';
import { scheduleTypeOptions } from '@src/utils/determine-schedule-type';

export default function ReasonForVisit({
	formId,
	setLoading,
}: Readonly<{ formId: string; setLoading: (loading: boolean) => void }>) {
	const router = useRouter();
	const logger = getLogger('client');
	const { timeSlot } = useTimeSlotStore((store) => store);

	const {
		form,
		revealSicknessOptions,
		helpModalOpen,
		facility,
		scheduleType,
		clientId,
		mainReasonForVisit,
		fromPAO,
		rfvChangeModalOpen,
		handleOpenModal,
		handleCloseModal,
		handleReasonForVisit,
		handleRFVChangeCancel,
		handleRFVChangeProceed,
		notes,
		setNotes,
		notesReasonForVisit,
		setNotesReasonForVisit,
		navToPriorRoute,
		advanceToNextRoute,
		setTimeSlot,
		setTimeSlotFacility,
	} = useReasonForVisitForm({ setLoading });

	const conflicts = useAppointmentConflict();
	const { getPet, selectedPet } = usePetsInformationStore((store) => store);

	const [conflictModalOpen, setConflictModalOpen] = useState(false);
	const [activeConflictAppointment, setActiveConflictAppointment] = useState<Appointment | null>(null);
	const [activeConflictType, setActiveConflictType] = useState<ReasonForVisitType | null>(null);

	const sickConflict = conflicts[ReasonForVisitType.SickNeeds] ?? null;
	const checkupConflict = conflicts[ReasonForVisitType.RoutineCheckUp] ?? null;
	const selectedPetName = selectedPet ? getPet(selectedPet)?.name ?? '' : '';

	// Derive user type from store rather than accepting it as a prop
	const userType = clientId ? 'visited_before' : 'new';

	function onSubmit(values: FieldValues) {
		if (setLoading) setLoading(true);
		const { visitReason, specificReasonForVisit } = values;
		const isSick = visitReason === 'sick';
		const reasonForVisitValue = isSick ? ReasonForVisitType.SickNeeds : ReasonForVisitType.RoutineCheckUp;
		const specificReasonValue = isSick ? specificReasonForVisit.join('|') : '';

		const conflict = conflicts[reasonForVisitValue];
		if (conflict) {
			setActiveConflictAppointment(conflict);
			setActiveConflictType(reasonForVisitValue);
			setConflictModalOpen(true);
			if (setLoading) setLoading(false);
			return;
		}

		dataLayerConfig({
			event: 'appointment_step',
			step_name: 'reason_for_visit_complete',
			hospital_id: facility?.id,
			hospital_name: facility?.displayName,
			reason_for_visit: reasonForVisitValue,
			specific_reason: specificReasonValue,
			visited_before: userType,
			visitor_type: 'new',
			scheduling: scheduleType.toLowerCase(),
		});

		// When the reason has actually changed, clear the time slot on closed/hybrid schedules only.
		// On open schedule the slot is chosen before reason, so changing reason must not invalidate it.
		// mainReasonForVisit === null means it was never set (e.g. PAO omitted ?visitReason), not that
		// it changed — so only clear when there was a prior reason to compare against.
		if (
			mainReasonForVisit !== null &&
			reasonForVisitValue !== mainReasonForVisit &&
			scheduleType !== scheduleTypeOptions.open
		) {
			setTimeSlot(null);
			setTimeSlotFacility(null);
		}

		if (reasonForVisitValue !== notesReasonForVisit) {
			// Main reason type changed — clear notes entirely
			setNotes({});
			setNotesReasonForVisit(null);
		} else if (isSick) {
			// Same main type — filter notes to keep only entries for still-selected reasons
			const selectedKeys = new Set(specificReasonForVisit.map((reason) => reason.replaceAll(',', '')));
			const filteredNotes = Object.fromEntries(Object.entries(notes).filter(([key]) => selectedKeys.has(key)));
			setNotes(filteredNotes);
		}

		let proceed = true;
		if (isSick || visitReason === 'checkup') {
			proceed = handleReasonForVisit({
				reasonType: reasonForVisitValue,
				visitReason,
				specificReasonForVisit: isSick ? specificReasonForVisit : [],
			});
		}

		if (!proceed) return;

		logger.info({
			name: '',
			category: 'ReasonForVisit Completed - appointment_step - at loggedIn',
			value: `client: ${clientId ?? 'new client'} with hospital_id: ${facility?.id},
			hospital_name: ${facility?.displayName},
			reason_for_visit: ${reasonForVisitValue},
			specific_reason: ${specificReasonValue},
			visited_before: ${userType},
			visitor_type: 'new',
			scheduling: ${scheduleType.toLowerCase()}`,
		});

		advanceToNextRoute((route: string) => router.push(route ?? '/'));
	}

	useEffect(() => {
		if (facility && !mainReasonForVisit) {
			dataLayerConfig({
				event: 'appointment_step',
				step_name: 'reason_for_visit_load',
				hospital_id: facility.id,
				hospital_name: facility.displayName,
				visitor_type: clientId ? 'existing' : 'new',
				scheduling: scheduleType.toLowerCase(),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [facility]);

	return (
		<Form formId={formId} form={form} onSubmit={onSubmit}>
			{fromPAO && timeSlot?.date && timeSlot?.time && (
				<PreselectedAppointmentDateAlert
					preselectedAppointmentDate={new Intl.DateTimeFormat('en-US', { dateStyle: 'full' }).format(
						new Date(`${timeSlot.date}T00:00:00`)
					)}
					preselectedAppointmentTime={get12HourTime(timeSlot.time)}
				/>
			)}
			<ReasonForVisitForm
				revealSicknessOptions={revealSicknessOptions}
				handleOpenModal={handleOpenModal}
				sickConflict={sickConflict}
				checkupConflict={checkupConflict}
			/>
			<IllnessExplanationModal open={helpModalOpen} handleClose={handleCloseModal} />
			<ChangeVisitReasonModal
				open={rfvChangeModalOpen}
				onProceed={() => {
					handleRFVChangeProceed();
					if (userType === 'visited_before') {
						navToPriorRoute(APPLICATION_ROUTES.appointmentSelection.route, (route: string) =>
							router.push(route)
						);
					}
				}}
				onCancel={handleRFVChangeCancel}
			/>
			{activeConflictAppointment && activeConflictType && (
				<AppointmentConflictDialog
					open={conflictModalOpen}
					onClose={() => setConflictModalOpen(false)}
					appointment={activeConflictAppointment}
					petName={selectedPetName}
				/>
			)}
		</Form>
	);
}
